/* eslint-disable max-lines */
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import * as Chart from 'chart.js';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { ModelsComponents } from 'src/app/models/models-components.model';
import { ComponentGroups } from 'src/app/models/viewmodels/component-groups.model';
import { ChartDataService } from 'src/app/services/chart-data/chart-data.service';
import { FdgChartService } from 'src/app/services/fdg-chart/fdg-chart.service';
import { TinterAdditionsService } from 'src/app/services/tinter-additions/tinter-additions.service';
import { ChartSlot, IDataSets, IFdgData, Iline } from '../../models/chart-fdg.model';
import { ChartFilters } from '../../models/chart-filters.model';
import { ChartZoom } from '../../models/chart-zoom.model';
import { TinterAddition } from '../../models/tinter-addition.model';
import { ExportClipboardParameter } from '../../services/export-to-clipboard/export-clipboard-parameter';
import { ZoomDir } from '../../shared/enums/zoom-directions.enum';
import { MobileCheck } from 'src/app/services/mobile-check/mobile-check.service';
import { Iproject } from '../../models/project.model';
import { ChartDefaults } from '../../shared/enums/chart-defaults.enum';
import { VectorService } from "../../services/vector/vector.service";
import {FDGchartComponent} from "./fdg-chart.component";
import {LabelChartType} from "../../shared/pipes/label-chart-type.pipe";
import {CanvasWrapperComponent} from "../../shared/components/canvas-wrapper/canvas-wrapper.component";
import {ChartDegComponent} from "../chart-deg/chart-deg.component";
import {TranslateModule} from "@ngx-translate/core";
import {LoaderComponent} from "../../shared/components/loader/loader.component";
import {TinterAdditionTooltipPipe} from "../../pipes/tinter-addition-tooltip/tinter-addition-tooltip.pipe";
import {DatePipe, NgClass} from "@angular/common";
import {MatIcon} from "@angular/material/icon";
import {MatMiniFabButton} from "@angular/material/button";
import {MatTooltip} from "@angular/material/tooltip";

/**
 * Displays a list of FDG charts (FarbDifferenzGrafik), contains chart types and default labels
 * Because of performance reasons the change detection for this branch is set to 'OnPush'.
 */
@Component({
  selector: 'colimo-fdg-chart-list-component',
  styleUrls: ['fdg-chart-list.component.scss'],
  templateUrl: 'fdg-chart-list.component.html',
  imports: [
    FDGchartComponent,
    LabelChartType,
    CanvasWrapperComponent,
    ChartDegComponent,
    TranslateModule,
    LoaderComponent,
    MatIcon,
    MatMiniFabButton,
    TinterAdditionTooltipPipe,
    DatePipe,
    MatTooltip,
    NgClass
  ]
})
export class FDGchartListComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public chartFilters: ChartFilters;
  @Input() public selectedAngles: boolean[];
  @Input() public activeComponents: ComponentGroups;
  @Input() public modelsComponents: ModelsComponents;
  @Input() public chartComponentColors: string[];
  @Input() public chartComponentBorderColors: string[];
  @Input() public chartMultiBatchColors: string[];
  @Input() public projects: Iproject[];
  @Output() public chartFiltersChange: EventEmitter<ChartFilters> = new EventEmitter();
  @Output() public chartDataChange: EventEmitter<IFdgData> = new EventEmitter();
  @Output() public tolerancesChanged: EventEmitter<boolean> = new EventEmitter();
  public scaleDirections$: Subject<ZoomDir>[] = [];
  public tinterAdditions$: Observable<TinterAddition[]>;
  public chartDataFDG$: Observable<IFdgData>;
  public isLoading = true;
  public isRenderingCharts = true;
  public page = 0;
  public exportClipboardParameter: ExportClipboardParameter;

  public chartSlots: ChartSlot[] = [];
  public chartData: Iline[][][] = [];

  public fdgData: IFdgData;
  public tinterAdds: TinterAddition[];
  public tinterAdditions: TinterAddition[][];
  // Default labels
  public lineChartAngles: string[] = [];
  // Default scale
  public scale = {
    max: 3,
    min: 3,
  };
  public ZoomDir = ZoomDir;
  public dataAvailable = false;

  private stop$: Subject<boolean> = new Subject();
  private timer: NodeJS.Timeout;
  public isSmallTablet: boolean;

  constructor(
    private cd: ChangeDetectorRef,
    private fdgChartService: FdgChartService,
    private chartDataService: ChartDataService,
    private tinterAdditionsService: TinterAdditionsService,
    private mobileCheck: MobileCheck,
    private readonly vectorService: VectorService,
  ) { }

  public ngOnInit(): void {
    this.scaleDirections$ = this.fdgChartService.initializeScaleDirections();
    this.chartSlots = this.fdgChartService.chartSlots;
    this.chartData = this.fdgChartService.chartData;
    // Declare options for toleranceLine Plugin
    Chart.defaults.set(ChartDefaults.TOLERANCE_OPTIONS, {
      color: ChartDefaults.DEFAULT_TOLERANCE_LINE_COLOR,
      lineWidth: 1,
      dashed: true,
      asSquare: false,
      xOffset: 0,
      yOffset: 0,
    });

    this.chartFilters.page = 0;
    this.getFdgData();

    this.mobileCheck.getScreenSize().pipe(takeUntil(this.stop$)).subscribe((device) => {
      this.isSmallTablet = device.isSmallTablet;
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.chartFilters && !changes.chartFilters.firstChange) {
      // #396475 Average of components for fdg chart
      if (changes.chartFilters.previousValue.fdgAverage !== changes.chartFilters.currentValue.fdgAverage) {
        // if average checkbox changed update current available fdg data without loading the data again from the server
        this.updateFdgData(this.fdgData, this.tinterAdds);
      } else if (this.chartFilters) {
        this.getFdgData(); // load fdg data from server
      }
    }
  }

  public ngOnDestroy(): void {
    clearTimeout(this.timer);
    this.stop$.next(true);
    this.stop$.unsubscribe();
  }

  public updateChartFilters(chartFilters: ChartFilters): void {
    this.chartFilters = chartFilters;
    this.chartFiltersChange.emit(this.chartFilters);
    this.getFdgData();
  }

  public onPageChange(page: number): void {
    this.updatePage(page);
  }

  /**
   * Scales each chart in a specific row based on the direction.
   */
  public scaleRow(direction: ZoomDir, row: string): void {
    this.fdgChartService.scaleRow(direction, row, this.chartSlots);
  }

  /**
   * Listen to charts updated event and set zoomInfo values.
   */
  public onChartsUpdated(event: ChartZoom): void {
    this.chartSlots[event.slotIndex].zoomInfo = event.zoomInfo;
    this.cd.detectChanges();
  }

  public onLastChartRendered(hasRendered: boolean): void {
    this.isRenderingCharts = false;
  }

  private getFdgData(): void {
    const TIMEOUT_IN_MILLISECONDS = 30000;
    this.isLoading = true;
    if (this.chartFilters.batchIds && this.chartFilters.lineIds) {
      this.chartDataFDG$ = this.chartDataService.getFdgDataMultiBatch(this.chartFilters, this.modelsComponents);
      this.tinterAdditions$ = this.tinterAdditionsService.getTinterAdditionsMultiBatch(
        this.chartFilters.batchIds,
        this.chartFilters.lineIds,
      );
      combineLatest([this.chartDataFDG$, this.tinterAdditions$])
        .pipe(
          takeUntil(this.stop$),
          map(([fdgData, tinterAdditions]) => {
            return { fdgData, tinterAdditions };
          }),
        )
        .subscribe((response: { fdgData: IFdgData; tinterAdditions: TinterAddition[] }) => {
          this.isLoading = false;
          if (response && this.fdgChartService.isFdgDataValid(response.fdgData) && response.tinterAdditions) {
            this.updateFdgData(response.fdgData, response.tinterAdditions);
          } else if (!this.timer) {
            this.timer = setTimeout(() => {
              this.isLoading = false;
              this.cd.detectChanges();
            }, TIMEOUT_IN_MILLISECONDS);
          }
        });

    } else {

      this.chartDataFDG$ = this.chartDataService.getFdgData(this.chartFilters, this.modelsComponents);
      this.tinterAdditions$ = this.tinterAdditionsService.getTinterAdditions(
        this.chartFilters.batchId,
        this.chartFilters.lineId,
      );
      combineLatest([this.chartDataFDG$, this.tinterAdditions$])
        .pipe(
          takeUntil(this.stop$),
          map(([fdgData, tinterAdditions]) => {
            return { fdgData, tinterAdditions };
          }),
        )
        .subscribe((response: { fdgData: IFdgData; tinterAdditions: TinterAddition[] }) => {
          this.isLoading = false;
          if (response && this.fdgChartService.isFdgDataValid(response.fdgData) && response.tinterAdditions) {
            this.updateFdgData(response.fdgData, response.tinterAdditions);
          } else if (!this.timer) {
            this.timer = setTimeout(() => {
              this.isLoading = false;
              this.cd.detectChanges();
            }, TIMEOUT_IN_MILLISECONDS);
          }
        });
    }
  }

  /**
   * Constructs 2-dim array of 3*10 slots for charts. Populates this 2-dim-array
   * with actual response data.
   *
   * @param data response containing data Object
   */
  private populateChartRows(data: IDataSets): void {
    this.fdgChartService.populateChartSlots(data);
    this.fdgChartService.populateChartData(data);
  }

  private buildExportClipboardParameter(response: IFdgData): void {
    this.exportClipboardParameter = {
      batchId: response.batchId,
      line: response.lineNumber,
      models: this.chartFilters?.models ?? '*',
      bodyComponents: this.chartFilters?.bodyComponents ?? '*',
      startDate: this.chartFilters?.startDate,
      endDate: this.chartFilters?.endDate
    };
  }

  private updatePage(page: number): void {
    this.isLoading = true;
    this.chartFilters.page = page;
    this.getFdgData();
    this.cd.detectChanges();
  }

  private updateFdgData(fdgDataResponse: IFdgData, tinterAdditionsResponse: TinterAddition[]): void {
    // save current data as standard to local variables
    // in case of average display toggle on/off we can calculate the averages from the local data instead of loading the server data again
    this.fdgData = fdgDataResponse;
    this.tinterAdds = tinterAdditionsResponse;

    // // #396475 Average of components for fdg chart - averages or standard display
    const fdgData = this.chartFilters.fdgAverage ? this.calculateComponentAverage(this.fdgData) : this.fdgData;

    // Set global Chart options for toleranceLine Plugin
    Chart.defaults.set(ChartDefaults.TOLERANCE_SHOW, { show: fdgData.tolerances });
    Chart.defaults.set(ChartDefaults.TOLERANCE_SCALE, fdgData.scaleTolerance ? fdgData.scaleTolerance : []);

    this.dataAvailable = true;

    this.scale.min = fdgData.scale[0];
    this.scale.max = fdgData.scale[1];

    this.lineChartAngles = fdgData.angles;

    if (this.chartFilters.tolerance === null || this.chartFilters.tolerance === undefined) {
      this.tolerancesChanged.emit(fdgData.tolerances);
    }

    this.buildExportClipboardParameter(fdgData);
    this.populateChartRows(fdgData.data);

    // Groups tinter additions by dates. Makes it easier to displaying the tinter additions.
    this.tinterAdditions = this.fdgChartService.getTinterAdditionsCorrespondingToDates(
      fdgData.startDate,
      fdgData, // #508837 Update tinter adds display for FDG plot
      tinterAdditionsResponse,
    );

    this.chartDataChange.emit(fdgData);
    this.cd.detectChanges();
  }

  // #396475 Average of components for fdg chart
  private calculateComponentAverage(fdgData: IFdgData): IFdgData {
    // create deep copy with empty data
    const avgFdg: IFdgData = JSON.parse(JSON.stringify(fdgData));
    avgFdg.data = {};

    const avgData: IDataSets = {};

    // iterate over different deltas
    Object.keys(fdgData.data).map((DX: string) => {

      const cur: Iline[][] = fdgData.data[DX].dataSets;

      // iterate over date index
      for (let dateIndex = 0; dateIndex < fdgData.dates.length; dateIndex++) {

        const curLines = cur[dateIndex];
        const dataMap: Map<string, Iline> = new Map();

        // sum up all values by iterating over lines and each angle - grouped by batchLineKey
        for (let lineIndex = 0; lineIndex < curLines.length; lineIndex++) {
          for (let angleIndex = 0; angleIndex < fdgData.angles.length; angleIndex++) {
            const key = curLines[lineIndex].batchLineKey + '-' + curLines[lineIndex].type; // batchLineKey and type as unique key
            if (!dataMap.has(key)) {
              // init empty data line on first key occurrence
              dataMap.set(key, {
                id: curLines[lineIndex].id,
                type: curLines[lineIndex].type,
                data: Array<number>(fdgData.angles.length).fill(0),
                batchLineKey: curLines[lineIndex].batchLineKey,
                component: 'AVG-' + curLines[lineIndex].type
              });
            }
            dataMap.get(key).data[angleIndex] += curLines[lineIndex].data[angleIndex];
          }
        }

        // divide by curLines.length for average calculation and round value
        for (let divideIndex = 0; divideIndex < fdgData.angles.length; divideIndex++) {
          dataMap.forEach((entry: Iline, key: string) => {
            entry.data[divideIndex] = this.vectorService.round(entry.data[divideIndex] / curLines.length, 1);
          });
        }

        if (!avgData[DX]) {
          avgData[DX] = { dataSets: [] };
        }
        avgData[DX].dataSets[dateIndex] = [];

        // add data calculated avg lines to average fdg data
        dataMap.forEach((entry: Iline, key: string) => {
          avgData[DX].dataSets[dateIndex].push(entry);
        });
      }
      return true;
    });
    avgFdg.data = avgData;
    return avgFdg;
  }

}
